/**
 * Colaborările (task #2516, cerut de Dorian pe 03.11.2026, legat de #2299): medici, clinici,
 * farmacii, branduri cu care firma are un contract, plus „mentenanța" lunară a celor pe termen lung.
 * E evidența lui Dorian, nu un raport obligatoriu: o lună fără înregistrare nu e o restanță, ci
 * doar o lună în care n-a notat nimic (Mădălin, 04.11.2026). O lună validată nu se mai schimbă.
 *
 * Regulile stau aici, o singură dată: API-ul le folosește la salvare și la raport, interfața la
 * afișarea lunilor. Două socoteli ale aceleiași luni ar da două rapoarte diferite.
 *
 * Lunile sunt text `AAAA-LL`, zilele `AAAA-LL-ZZ`. Nu se trece prin `Date` local: o zi fără oră
 * citită în fusul României poate sări în luna de dinainte.
 */
export const TIPURI_COLABORARE = ['medic', 'clinica', 'farmacie', 'brand', 'altul'];
export const ETICHETE_TIP_COLABORARE = {
    medic: 'Medic',
    clinica: 'Clinică',
    farmacie: 'Farmacie',
    brand: 'Brand',
    altul: 'Altul',
};
/**
 * Durata, din #2299 (Dorian, 24.10.2026): „contract one time, contract long time". Doar cele pe
 * termen lung au mentenanță lunară; cele de o singură dată contează doar la luna semnării.
 */
export const DURATE_COLABORARE = ['continua', 'o_data'];
export const ETICHETE_DURATA_COLABORARE = {
    continua: 'Pe termen lung (mentenanță lunară)',
    o_data: 'O singură dată',
};
export const STARI_COLABORARE = ['negociere', 'activ', 'suspendat', 'incheiat'];
export const ETICHETE_STARE_COLABORARE = {
    negociere: 'În negociere',
    activ: 'Activă',
    suspendat: 'Suspendată',
    incheiat: 'Încheiată',
};
/** Unde se poate merge din fiecare stare. „Încheiată" e capăt: o reluare e o colaborare nouă. */
export const TRANZITII_COLABORARE = {
    negociere: ['activ', 'incheiat'],
    activ: ['suspendat', 'incheiat'],
    suspendat: ['activ', 'incheiat'],
    incheiat: [],
};
export function poateTrece(din, spre) {
    return TRANZITII_COLABORARE[din].includes(spre);
}
const RE_LUNA = /^\d{4}-(0[1-9]|1[0-2])$/;
const RE_ZI = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;
export function esteLuna(s) {
    return RE_LUNA.test(s);
}
export function esteZi(s) {
    return RE_ZI.test(s);
}
/** `2026-10-15` → `2026-10`. Pe text, fără `Date`. */
export function lunaDinZi(zi) {
    return zi.slice(0, 7);
}
/** Luna de după: `2026-12` → `2027-01`. */
export function lunaUrmatoare(luna) {
    const an = Number(luna.slice(0, 4));
    const l = Number(luna.slice(5, 7));
    return l === 12 ? `${an + 1}-01` : `${an}-${String(l + 1).padStart(2, '0')}`;
}
/** Luna de dinainte: `2027-01` → `2026-12`. */
export function lunaPrecedenta(luna) {
    const an = Number(luna.slice(0, 4));
    const l = Number(luna.slice(5, 7));
    return l === 1 ? `${an - 1}-12` : `${an}-${String(l - 1).padStart(2, '0')}`;
}
/** Toate lunile de la `de` la `pana`, inclusiv. Gol dacă `pana` e înainte de `de`. */
export function luniIntre(de, pana) {
    const luni = [];
    for (let l = de; l <= pana; l = lunaUrmatoare(l))
        luni.push(l);
    return luni;
}
/** Prima și ultima zi a lunii, ca text. Ultima zi ține cont de februarie. */
export function capeteLuna(luna) {
    const an = Number(luna.slice(0, 4));
    const l = Number(luna.slice(5, 7));
    const zile = new Date(Date.UTC(an, l, 0)).getUTCDate();
    return { prima: `${luna}-01`, ultima: `${luna}-${String(zile).padStart(2, '0')}` };
}
/** Ziua de dinainte, pe text (`2026-03-01` → `2026-02-28`). */
function ziuaDinainte(zi) {
    const d = new Date(`${zi}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() - 1);
    return d.toISOString().slice(0, 10);
}
const NUME_LUNI = [
    'ianuarie',
    'februarie',
    'martie',
    'aprilie',
    'mai',
    'iunie',
    'iulie',
    'august',
    'septembrie',
    'octombrie',
    'noiembrie',
    'decembrie',
];
/** `2026-10` → `octombrie 2026`. */
export function lunaInCuvinteColaborare(luna) {
    const nume = NUME_LUNI[Number(luna.slice(5, 7)) - 1];
    return nume ? `${nume} ${luna.slice(0, 4)}` : luna;
}
/**
 * Lunile în care colaborarea a fost ACTIVĂ măcar o zi, în perioada contractului, până la
 * `panaLa` inclusiv. Pe ele se cere mentenanța.
 *
 * Se citește din istoricul stărilor, nu din starea de azi: o colaborare suspendată din decembrie
 * a fost activă în octombrie și noiembrie, iar lunile acelea trebuie completate în continuare.
 * O lună în care a fost și activă, și suspendată se cere (a existat o zi activă).
 */
export function luniActive(istoric, perioada, panaLa) {
    const pasi = [...istoric].sort((a, b) => (a.dela < b.dela ? -1 : a.dela > b.dela ? 1 : 0));
    const luni = new Set();
    for (let i = 0; i < pasi.length; i++) {
        const pas = pasi[i];
        if (pas.stare !== 'activ')
            continue;
        const urmator = pasi[i + 1];
        // Intervalul activ: de la `dela` până în ziua dinaintea schimbării următoare.
        let de = pas.dela;
        let pana = urmator ? ziuaDinainte(urmator.dela) : null;
        if (de < perioada.valabilDe)
            de = perioada.valabilDe;
        if (perioada.valabilPana && (pana === null || pana > perioada.valabilPana))
            pana = perioada.valabilPana;
        if (pana !== null && pana < de)
            continue;
        const ultimaLuna = pana === null ? panaLa : lunaDinZi(pana) < panaLa ? lunaDinZi(pana) : panaLa;
        for (const l of luniIntre(lunaDinZi(de), ultimaLuna))
            luni.add(l);
    }
    return [...luni].sort();
}
export const ETICHETE_STARE_LUNA = {
    cu_activitate: 'Cu activitate',
    fara_activitate: 'Fără activitate',
    fara_inregistrare: 'Fără înregistrare',
    in_curs: 'Luna în curs',
};
/**
 * Fiecare lună în care colaborarea a fost activă, cu starea ei. O lună trecută fără rând e
 * „fără înregistrare" — neutru, nu o restanță. Luna curentă fără rând e „în curs". Lunile completate în afara celor cerute (o notă pusă pe o lună
 * de suspendare) rămân pe ecran, ca nimic scris să nu dispară.
 */
export function luniColaborare(ceruteLuni, completate, lunaCurenta) {
    const toate = new Set([...ceruteLuni, ...completate.keys()]);
    const cerute = new Set(ceruteLuni);
    return [...toate].sort().map((luna) => {
        const are = completate.get(luna);
        const stare = are === true
            ? 'cu_activitate'
            : are === false
                ? 'fara_activitate'
                : luna >= lunaCurenta
                    ? 'in_curs'
                    : 'fara_inregistrare';
        return { luna, stare, ceruta: cerute.has(luna) };
    });
}
/**
 * Raportul lunar al colaborărilor. Trei liste, fiecare colaborare cel mult o dată în fiecare:
 *  - `contracteNoi` — semnate în lună (și activate vreodată), orice durată;
 *  - `cuActivitate` — pe termen lung, active în lună, cu mentenanța „cu activitate";
 *  - `faraActivitate` — pe termen lung, active în lună, marcate „fără activitate" SAU fără niciun
 *    rând (`faraInregistrare`). O lună nenotată se socotește fără activitate, inclusiv la
 *    validare — nu blochează nimic.
 */
export function raportLunar(luna, colaborari) {
    const rand = (c) => ({
        id: c.id,
        tip: c.tip,
        denumire: c.denumire,
        responsabil: c.responsabil,
        obiect: c.obiect,
        valoare: c.valoare,
        dataSemnare: c.dataSemnare,
        descriere: c.luna?.descriere ?? null,
        dataActivitate: c.luna?.dataActivitate ?? null,
        faraInregistrare: !c.luna,
    });
    const contracteNoi = [];
    const cuActivitate = [];
    const faraActivitate = [];
    for (const c of colaborari) {
        // Semnat = a fost activa macar o zi. O data a semnarii trecuta in negociere, pe o colaborare
        // incheiata fara sa porneasca, nu e un contract nou (verificare #1047, R1).
        const semnata = c.istoric.some((p) => p.stare === 'activ');
        if (semnata && c.dataSemnare && lunaDinZi(c.dataSemnare) === luna)
            contracteNoi.push(rand(c));
        if (c.durata !== 'continua')
            continue;
        const activa = luniActive(c.istoric, { valabilDe: c.valabilDe, valabilPana: c.valabilPana }, luna).includes(luna);
        if (!activa)
            continue;
        if (c.luna?.areActivitate)
            cuActivitate.push(rand(c));
        else
            faraActivitate.push(rand(c));
    }
    const dupaNume = (a, b) => a.denumire.localeCompare(b.denumire, 'ro');
    contracteNoi.sort(dupaNume);
    cuActivitate.sort(dupaNume);
    faraActivitate.sort(dupaNume);
    const valoareNoi = contracteNoi.reduce((s, r) => s + (r.valoare ? Number(r.valoare) : 0), 0);
    const peTip = (lista) => {
        const m = {};
        for (const r of lista)
            m[r.tip] = (m[r.tip] ?? 0) + 1;
        return m;
    };
    return {
        luna,
        contracteNoi,
        cuActivitate,
        faraActivitate,
        indicatori: {
            contracteNoi: contracteNoi.length,
            contracteNoiPeTip: peTip(contracteNoi),
            /** Suma valorilor trecute pe contractele noi, lei. Cele fără valoare contează 0. */
            valoareContracteNoi: Math.round(valoareNoi * 100) / 100,
            activeCuActivitate: cuActivitate.length,
            activeFaraActivitate: faraActivitate.length,
            /** Din cele fără activitate, câte n-au avut rând deloc. */
            faraInregistrare: faraActivitate.filter((r) => r.faraInregistrare).length,
        },
    };
}
//# sourceMappingURL=colaborari.js.map